/* VSKLO native subcollection rail: responsive progress and accessible navigation. */
(() => {
  'use strict';
  const ROOT = '[data-vsklo-subcollections]';
  const CARD = '.vsklo-subcollections-wide__card';
  const BUTTON = 'button[data-subcollection-index]';

  function init(root) {
    if (!root || root.dataset.vskloSubcollectionsReady === '1') return;
    const rail = root.querySelector('[data-vsklo-subcollections-track]');
    const nav = root.querySelector('[data-vsklo-subcollections-nav]');
    if (!rail || !nav) return;
    const cards = Array.from(rail.querySelectorAll(CARD));
    const buttons = Array.from(nav.querySelectorAll(BUTTON));
    if (cards.length < 2 || buttons.length !== cards.length) return;
    root.dataset.vskloSubcollectionsReady = '1';

    const mobile = window.matchMedia('(max-width: 749px)');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    let pressed = null;
    let pressX = 0;
    let pressY = 0;
    // Keep native mobile scrolling at the first card until an actual horizontal
    // gesture, dot navigation, or keyboard action. Late browser scroll restoration
    // and vertical page scrolling must not move the carousel to the second card.
    let userInteracted = false;
    let wasMobile = mobile.matches;
    let revealObserver = null;

    function resetToFirst() {
      if (!mobile.matches || userInteracted || !rail.isConnected) return;
      // Prevent the browser from snapping back to a previously restored card.
      rail.style.scrollSnapType = 'none';
      if (rail.scrollLeft !== 0) rail.scrollLeft = 0;
      setActive(0);
    }

    function registerInteraction() {
      if (!mobile.matches) return;
      userInteracted = true;
      // Enable snapping only after a deliberate rail interaction.
      rail.style.scrollSnapType = 'x mandatory';
      if (revealObserver) {
        revealObserver.disconnect();
        revealObserver = null;
      }
    }

    function onPageShow(event) {
      if (!mobile.matches) return;
      // BFCache can preserve the prior swipe state and a restored second card.
      if (event && event.type === 'pageshow' && event.persisted) userInteracted = false;
      if (userInteracted) return;
      resetToFirst();
      window.requestAnimationFrame(() => {
        resetToFirst();
        window.requestAnimationFrame(resetToFirst);
      });
    }

    function setActive(index) {
      buttons.forEach((button, i) => {
        button.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    function closestIndex() {
      // The final card cannot always align to the left on wider phones.
      // At the rail's physical end, the last progress button must be active.
      const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
      if (maxScroll > 3 && rail.scrollLeft >= maxScroll - 4) return cards.length - 1;
      const railLeft = rail.getBoundingClientRect().left;
      const inset = parseFloat(window.getComputedStyle(rail).paddingLeft) || 0;
      let current = 0;
      let bestDistance = Infinity;
      cards.forEach((card, index) => {
        const distance = Math.abs(card.getBoundingClientRect().left - railLeft - inset);
        if (distance < bestDistance) {
          bestDistance = distance;
          current = index;
        }
      });
      return current;
    }

    function sync() {
      if (mobile.matches && !nav.hidden) setActive(closestIndex());
    }

    function release() {
      if (pressed) pressed.classList.remove('is-pressed');
      pressed = null;
    }

    function onScroll() {
      release();
      if (mobile.matches && !userInteracted) {
        // Covers asynchronous history restoration after initial page rendering.
        resetToFirst();
        return;
      }
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        sync();
      });
    }

    function measure() {
      const style = window.getComputedStyle(rail);
      const gap = parseFloat(style.columnGap || style.gap) || 0;
      const inset = parseFloat(style.paddingLeft) || 0;
      const totalWidth = cards.reduce((total, card) => total + card.getBoundingClientRect().width, 0)
        + gap * (cards.length - 1);
      const overflowing = mobile.matches && totalWidth > rail.clientWidth - inset - 3;
      nav.hidden = !overflowing;
      root.classList.toggle('vsklo-subcollections-wide--static', mobile.matches && !overflowing);
      if (wasMobile !== mobile.matches) {
        wasMobile = mobile.matches;
        userInteracted = false;
        if (!mobile.matches) rail.style.scrollSnapType = '';
      }
      if (overflowing) {
        rail.setAttribute('aria-roledescription', 'carousel');
        if (userInteracted) {
          rail.style.scrollSnapType = 'x mandatory';
          sync();
        } else resetToFirst();
      } else {
        rail.removeAttribute('aria-roledescription');
        rail.style.scrollSnapType = '';
        setActive(0);
      }
    }

    function goTo(index) {
      if (!mobile.matches || nav.hidden || !cards[index]) return;
      const inset = parseFloat(window.getComputedStyle(rail).paddingLeft) || 0;
      const destination = rail.scrollLeft + cards[index].getBoundingClientRect().left
        - rail.getBoundingClientRect().left - inset;
      setActive(index);
      rail.scrollTo({
        left: destination,
        behavior: reduceMotion.matches ? 'auto' : 'smooth'
      });
    }

    nav.addEventListener('click', (event) => {
      const button = event.target.closest(BUTTON);
      if (!button || !nav.contains(button)) return;
      registerInteraction();
      goTo(Number(button.dataset.subcollectionIndex));
    });

    nav.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const current = buttons.indexOf(document.activeElement);
      if (current < 0) return;
      event.preventDefault();
      registerInteraction();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
        : (current + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
      buttons[next].focus();
      goTo(next);
    });

    rail.addEventListener('scroll', onScroll, { passive: true });
    rail.addEventListener('wheel', (event) => {
      // Vertical wheel motion is page scrolling, NOT a carousel interaction.
      if (Math.abs(event.deltaX) > 4 && Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        registerInteraction();
      }
    }, { passive: true });
    rail.addEventListener('keydown', (event) => {
      if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown', 'Tab'].includes(event.key)) {
        registerInteraction();
      }
    });
    // Do not mistake browser-restored focus for a genuine customer swipe.

    // Touch events remain reliable on iOS even after pointercancel from panning.
    let touchOrigin = null;
    rail.addEventListener('touchstart', (event) => {
      const touch = event.touches[0];
      touchOrigin = touch ? { x: touch.clientX, y: touch.clientY } : null;
    }, { passive: true });
    rail.addEventListener('touchmove', (event) => {
      if (!touchOrigin || userInteracted) return;
      const touch = event.touches[0];
      if (!touch) return;
      const dx = Math.abs(touch.clientX - touchOrigin.x);
      const dy = Math.abs(touch.clientY - touchOrigin.y);
      if (dx > 7 && dx > dy * 1.2) registerInteraction();
    }, { passive: true });
    const finishTouch = () => { touchOrigin = null; };
    rail.addEventListener('touchend', finishTouch, { passive: true });
    rail.addEventListener('touchcancel', finishTouch, { passive: true });

    rail.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.target === rail) registerInteraction();
      if (event.pointerType !== 'touch' || !event.isPrimary) return;
      const card = event.target.closest(CARD);
      if (!card || !rail.contains(card)) return;
      release();
      pressed = card;
      pressX = event.clientX;
      pressY = event.clientY;
      card.classList.add('is-pressed');
    }, { passive: true });
    rail.addEventListener('pointermove', (event) => {
      if (pressed && Math.hypot(event.clientX - pressX, event.clientY - pressY) > 10) release();
    }, { passive: true });
    rail.addEventListener('pointerup', release, { passive: true });
    rail.addEventListener('pointercancel', release, { passive: true });
    rail.addEventListener('pointerleave', release, { passive: true });

    let observer;
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(() => window.requestAnimationFrame(measure));
      observer.observe(rail);
    }
    const resize = () => window.requestAnimationFrame(measure);
    window.addEventListener('resize', resize, { passive: true });
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('load', onPageShow, { once: true });
    if ('IntersectionObserver' in window) {
      revealObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onPageShow();
      }, { threshold: 0.01 });
      revealObserver.observe(rail);
    }
    root.addEventListener('vsklo:dispose', () => {
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('load', onPageShow);
      window.removeEventListener('resize', resize);
      if (observer) observer.disconnect();
      if (revealObserver) revealObserver.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
      release();
    }, { once: true });
    window.requestAnimationFrame(measure);
  }

  function boot(scope) {
    if (scope.matches && scope.matches(ROOT)) init(scope);
    scope.querySelectorAll(ROOT).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(ROOT).forEach((root) => root.dispatchEvent(new Event('vsklo:dispose')));
  });
})();